import { useState, type FormEvent } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, resetClientSecurityState, unwrap } from '@/api/client';
import { isApiError } from '@/api/errors';
import { useOperationImplemented } from '@/app/health';
import { Button, Field, TextInput } from '@/components/ui';
import { Wordmark } from '@/components/brand/Logo';

function safeRedirect(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  // Only same-origin private paths; never protocol-relative or external targets.
  if (!value.startsWith('/app') || value.startsWith('//') || value.includes('\\')) return undefined;
  return value;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({ redirect: safeRedirect(search.redirect) }),
  head: () => ({ meta: [{ title: 'Sign in · Eryce' }, { name: 'robots', content: 'noindex' }] }),
  component: LoginPage,
});

function failureText(err: unknown): string {
  if (!isApiError(err)) return 'Sign in did not complete. Try again.';
  if (err.notImplemented) return 'Sign in not built on the server yet.';
  if (err.status === 429) return `Too many attempts. Wait ${err.retryAfterSeconds ? `${err.retryAfterSeconds} seconds` : 'a moment'} and try again.`;
  if (err.status === 401 || err.status === 422 || err.status === 400) return 'Email, username or password is not correct.';
  if (err.status === 0) return 'The server did not respond. Try again shortly.';
  return 'Sign in did not complete. Try again.';
}

function LoginPage() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const unbuilt = useOperationImplemented('login') === false;

  const login = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/auth/login', { body: { username, password } })),
    onSuccess: async () => {
      resetClientSecurityState();
      qc.removeQueries({ queryKey: ['org'] });
      await qc.invalidateQueries({ queryKey: ['session'] });
      void navigate({ to: redirect ?? '/app', replace: true });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!username || !password) return;
    login.mutate();
  };

  const error = unbuilt ? 'Sign in not built on the server yet.' : login.error ? failureText(login.error) : null;

  return (
    <main className="auth-wrap">
      <form className="auth-form" onSubmit={onSubmit} noValidate aria-labelledby="login-title">
        <h1 id="login-title" className="wordmark">
          <Wordmark />
        </h1>
        <div className="stack">
          <Field label="Email or username">
            <TextInput
              name="username"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </Field>
          <Field label="Password">
            <TextInput
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Button type="submit" variant="primary" className="btn-block" loading={login.isPending} disabled={unbuilt || !username || !password}>
            Sign in
          </Button>
          <p className="auth-error" role="alert">
            {error}
          </p>
        </div>
      </form>
    </main>
  );
}
